import { useEffect, useRef, useState, type ReactNode } from 'react';

interface LandingLazyLoadProps {
  children: ReactNode;
  offset?: number;
  once?: boolean;
}

const isTestMode = import.meta.env.MODE === 'test';

export function LandingLazyLoad({ children, offset = 0, once = true }: LandingLazyLoadProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(isTestMode);

  useEffect(() => {
    if (isTestMode) {
      return undefined;
    }

    const node = ref.current;

    if (!node || (once && visible)) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);

          if (once) {
            observer.disconnect();
          }
        } else if (!once) {
          setVisible(false);
        }
      },
      { rootMargin: `${offset}px` },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [offset, once, visible]);

  if (isTestMode) {
    return <>{children}</>;
  }

  return <div ref={ref}>{visible ? children : null}</div>;
}
